import * as React from 'react';

import { useSettingsLocation } from '@rematter/modules/settings/_state/settingsModule.state';
import {
  Button,
  Col,
  Menu,
  Row,
  SearchField,
  TinyTable,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideBuilding,
  LucideComponent,
  LucideEllipsis,
  LucidePlusCircle,
  LucideSquarePen,
  LucideTrash,
} from 'lucide-react';

import { FacilityGroupRecord } from '../../_graphql/queryFacilityGroups';
import * as locationsState from '../../_state/locations.state';
import { SettingsContent } from '../_shared/SettingsContent';

import { LocationGroupCreationDialog } from './LocationGroupCreationDialog';

type FacilityGroupRowMenuProps = { row: FacilityGroupRecord };

const FacilityGroupRowMenu = (props: FacilityGroupRowMenuProps) => {
  const [, setLocation] = useSettingsLocation();
  const deleteFacilityGroup = useSetAtom(locationsState.$deleteFacilityGroup);

  const handleDelete = async () => {
    try {
      await deleteFacilityGroup({ id: props.row.id });
    } catch (error) {
      console.error('Failed to delete facility group:', error);
    }
  };

  return (
    <div className={'@FacilityGroupRowMenu contents'}>
      <Menu
        trigger={
          <Button
            className={'px-2'}
            size="small"
            color="neutral"
            variant="outline"
            icon={<LucideEllipsis />}
          />
        }
        items={[
          {
            label: 'Edit',
            icon: <LucideSquarePen />,
            onAction: () => setLocation(`/locations/groups/${props.row.id}`),
          },
          {
            label: 'Delete',
            icon: <LucideTrash />,
            className: 'text-failure',
            secondStep: ({ close }) => (
              <Col gap3>
                <div className="text-center text-sm text-neutral/70">
                  Are you sure?
                </div>
                <Row gap2>
                  <Button variant="outline" color="neutral" onPress={close}>
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    color="failure"
                    icon={<LucideTrash />}
                    onPress={() => {
                      close();
                      handleDelete();
                    }}
                  >
                    Yes, delete
                  </Button>
                </Row>
              </Col>
            ),
          },
        ]}
      />
    </div>
  );
};

export const LocationGroupList = () => {
  const [, setLocation] = useSettingsLocation();
  const facilityGroupsList = useAtomValue(locationsState.$facilityGroupsList);
  const [searchTerm, setSearchTerm] = useAtom(
    locationsState.$groupSearchTerm.value,
  );
  const setIsAddFacilityGroupDialogOpen = useSetAtom(
    locationsState.$isAddFacilityGroupDialogOpen,
  );

  return (
    <SettingsContent className="@LocationGroupList h-full">
      <Col gap4 className="h-full">
        <Row gap2>
          <SearchField value={searchTerm} onChange={setSearchTerm} autoFocus />
          <Button
            className={'ml-auto'}
            icon={<LucidePlusCircle />}
            onPress={() => setIsAddFacilityGroupDialogOpen(true)}
          >
            Add Group
          </Button>
        </Row>
        <LocationGroupCreationDialog />
        <TinyTable
          rowKey={(row) => row.id}
          selectable
          isLoading={facilityGroupsList.isLoading}
          error={facilityGroupsList.error}
          onRowClick={(row) => setLocation(`/locations/groups/${row.id}`)}
          columns={[
            {
              key: 'name',
              label: 'Group Name',
              icon: LucideComponent,
              cell: (row) => row.name,
            },
            {
              key: 'facilities',
              label: 'Facilities',
              icon: LucideBuilding,
              cell: (row) => (
                <Col gap1>
                  {row.facilities.map((facility) => (
                    <Row key={facility.id} gap2 className="items-center">
                      <LucideBuilding className="h-4 w-4 text-neutral/60" />
                      <span className="text-sm">{facility.name}</span>
                    </Row>
                  ))}
                  {row.facilities.length === 0 && (
                    <span className="text-sm text-neutral/50">–</span>
                  )}
                </Col>
              ),
            },
            {
              key: 'actions',
              label: null,
              preventRowClick: true,
              width: 'max-content',
              cell: (row) => <FacilityGroupRowMenu row={row} />,
            },
          ]}
          data={facilityGroupsList.data ?? []}
        />
      </Col>
    </SettingsContent>
  );
};
